import { ReplyComposeModal } from "components/ReplyComposeModal";
import { Button } from "components/ui/Button";
import type { ReplyComposeSeed } from "hooks/use-email-compose";
import type { PipelineEmailTimelineItem } from "hooks/use-pipeline-email-api";
import { restClient } from "lib/rest-client";
import { type FC, useState } from "react";
import { toast } from "react-toastify";

export type QuickReplyButtonProps = {
  pipelineId: string;
  /** focus_matter_id があれば送信時に透過 */
  focusMatterId?: string | null;
  /** タイムラインが空だった場合のフォールバック件名 */
  fallbackSubject?: string;
  /** 送信成功時に呼ぶ */
  onSent?: () => void;
};

type TimelineResponse = {
  items: PipelineEmailTimelineItem[];
};

/**
 * Sales Board からクイック返信を起動するボタン。
 * 押下時にタイムラインを lazy fetch し、最新メール相手を宛先にしてモーダルを開く。
 * @param {QuickReplyButtonProps} props props
 * @return {JSX.Element} ボタン + モーダル
 */
export const QuickReplyButton: FC<QuickReplyButtonProps> = ({
  pipelineId,
  focusMatterId,
  fallbackSubject,
  onSent,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [seed, setSeed] = useState<ReplyComposeSeed | null>(null);
  const [isPreparing, setIsPreparing] = useState(false);

  /**
   * タイムラインから返信シードを構築する
   * @return {Promise<void>}
   */
  const handleOpen = async (): Promise<void> => {
    setIsPreparing(true);
    try {
      const response = await restClient.get<TimelineResponse>(
        `/sales-pipelines/${pipelineId}/email-timeline`,
      );
      const items = response.items;
      // 最新 received → なければ最新 sent → なければ fallback
      const latestReceived = [...items]
        .reverse()
        .find(i => i.direction === "received");
      const latestSent = [...items].reverse().find(i => i.direction === "sent");
      const chosen = latestReceived ?? latestSent;

      if (!chosen) {
        toast.info(
          "このパイプラインにはまだメールがありません。宛先を入力してから送信してください",
        );
        setSeed({
          toEmail: "",
          subject: fallbackSubject ?? "",
          salesPipelineId: pipelineId,
          focusMatterId: focusMatterId ?? null,
        });
      } else {
        setSeed({
          toEmail: chosen.fromTo,
          subject: chosen.subject || fallbackSubject || "",
          salesPipelineId: pipelineId,
          focusMatterId: focusMatterId ?? null,
        });
      }
      setIsOpen(true);
    } catch {
      toast.error("返信の準備に失敗しました");
    } finally {
      setIsPreparing(false);
    }
  };

  return (
    <>
      <Button
        type="button"
        size="xs"
        variant="outline"
        onClick={handleOpen}
        disabled={isPreparing}
      >
        {isPreparing ? "準備中..." : "クイック返信"}
      </Button>
      <ReplyComposeModal
        isOpen={isOpen}
        seed={seed}
        onClose={() => setIsOpen(false)}
        onSent={onSent}
      />
    </>
  );
};
